import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationSchema from "_shared/_api/queries/pagination/_schema/pagination-parameters-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { TeamTemplateOrderField } from "work/team-templates/_api/queries/team-template-order-field";
import { TeamTemplateParty } from "work/team-templates/team-template-party";
import {
  TeamTemplateFilterParameters,
  TeamTemplateOrderParameters,
} from "work/team-templates/_api/queries/team-template-query";
import z from "zod";

export type TeamTemplateFilterParametersJSON = z.infer<
  typeof TeamTemplateFilterParametersSchema.TeamTemplateFilterParametersJSONZ
>;
export type TeamTemplateFilterParametersParams = z.infer<
  typeof TeamTemplateFilterParametersSchema.TeamTemplateFilterParametersZ
>;

export type TeamTemplateOrderParametersJSON = z.infer<
  typeof TeamTemplateOrderParametersSchema.TeamTemplateOrderParametersJSONZ
>;
export type TeamTemplateOrderParametersParams = z.infer<
  typeof TeamTemplateOrderParametersSchema.TeamTemplateOrderParametersZ
>;

export type TeamTemplateQueryJSON = z.infer<typeof TeamTemplateQuerySchema.TeamTemplateQueryJSONZ>;
export type TeamTemplateQueryParams = z.infer<typeof TeamTemplateQuerySchema.TeamTemplateQueryZ>;

export class TeamTemplateFilterParametersSchema {
  public static readonly type = z.custom<TeamTemplateFilterParameters>(
    (data) => data instanceof TeamTemplateFilterParameters,
    "Not a valid TeamTemplateFilterParameters instance"
  );

  public static readonly TeamTemplateFilterParametersJSONZ = z.object({
    minCreated: z.iso.datetime({ offset: true }).nullish(),
    maxCreated: z.iso.datetime({ offset: true }).nullish(),
    name: z.string().nullish(),
    creatorUserId: z.uuid().nullish(),
    creatorEntityId: z.uuid().nullish(),
    leaderUserId: z.uuid().nullish(),
    leaderEntityId: z.uuid().nullish(),
    teamMemberUserId: z.uuid().nullish(),
    teamMemberEntityId: z.uuid().nullish(),
    marketplaceId: z.uuid().nullish(),
    party: z.enum(TeamTemplateParty).nullish(),
    isArchived: z.boolean().nullish(),
    minArchived: z.iso.datetime({ offset: true }).nullish(),
    maxArchived: z.iso.datetime({ offset: true }).nullish(),
  });

  public static readonly TeamTemplateFilterParametersZ = z.object({
    minCreated: z.instanceof(AHDate).nullish(),
    maxCreated: z.instanceof(AHDate).nullish(),
    name: z.string().nullish(),
    creatorUserId: z.instanceof(Guid).nullish(),
    creatorEntityId: z.instanceof(Guid).nullish(),
    leaderUserId: z.instanceof(Guid).nullish(),
    leaderEntityId: z.instanceof(Guid).nullish(),
    teamMemberUserId: z.instanceof(Guid).nullish(),
    teamMemberEntityId: z.instanceof(Guid).nullish(),
    marketplaceId: z.instanceof(Guid).nullish(),
    party: z.enum(TeamTemplateParty).nullish(),
    isArchived: z.boolean().nullish(),
    minArchived: z.instanceof(AHDate).nullish(),
    maxArchived: z.instanceof(AHDate).nullish(),
  });

  public static readonly codec = z.codec(
    TeamTemplateFilterParametersSchema.TeamTemplateFilterParametersJSONZ,
    TeamTemplateFilterParametersSchema.TeamTemplateFilterParametersZ,
    {
      decode: (data) => ({
        minCreated: AHDate.fromJSON(data.minCreated) ?? undefined,
        maxCreated: AHDate.fromJSON(data.maxCreated) ?? undefined,
        name: data.name ? data.name : undefined,
        creatorUserId: Guid.fromJSON(data.creatorUserId) ?? undefined,
        creatorEntityId: Guid.fromJSON(data.creatorEntityId) ?? undefined,
        leaderUserId: Guid.fromJSON(data.leaderUserId) ?? undefined,
        leaderEntityId: Guid.fromJSON(data.leaderEntityId) ?? undefined,
        teamMemberUserId: Guid.fromJSON(data.teamMemberUserId) ?? undefined,
        teamMemberEntityId: Guid.fromJSON(data.teamMemberEntityId) ?? undefined,
        marketplaceId: Guid.fromJSON(data.marketplaceId) ?? undefined,
        party: data.party ?? undefined,
        isArchived: data.isArchived,
        minArchived: AHDate.fromJSON(data.minArchived) ?? undefined,
        maxArchived: AHDate.fromJSON(data.maxArchived) ?? undefined,
      }),
      encode: (params) => ({
        minCreated: params.minCreated?.toJSON(),
        maxCreated: params.maxCreated?.toJSON(),
        name: params.name,
        creatorUserId: params.creatorUserId?.toJSON(),
        creatorEntityId: params.creatorEntityId?.toJSON(),
        leaderUserId: params.leaderUserId?.toJSON(),
        leaderEntityId: params.leaderEntityId?.toJSON(),
        teamMemberUserId: params.teamMemberUserId?.toJSON(),
        teamMemberEntityId: params.teamMemberEntityId?.toJSON(),
        marketplaceId: params.marketplaceId?.toJSON(),
        party: params.party,
        isArchived: params.isArchived,
        minArchived: params.minArchived?.toJSON(),
        maxArchived: params.maxArchived?.toJSON(),
      }),
    }
  );

  public static validate(values: unknown): TeamTemplateFilterParametersParams {
    return parseOrThrow(TeamTemplateFilterParametersSchema.TeamTemplateFilterParametersZ, values, {
      logError: true,
      schemaName: "TeamTemplateFilterParametersSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): TeamTemplateFilterParametersJSON {
      return parseOrThrow(
        TeamTemplateFilterParametersSchema.TeamTemplateFilterParametersJSONZ,
        values,
        { 
          logError: true,
          schemaName: "TeamTemplateFilterParametersSchema",
          operation: "validateJSON",
          payloadSample: values 
        }
      );
    }

  public static deserialize(json: unknown): TeamTemplateFilterParametersParams | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(TeamTemplateFilterParametersSchema.TeamTemplateFilterParametersJSONZ, json, {
      logError: true,
      schemaName: "TeamTemplateFilterParametersSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return this.codec.decode(parsed);
  }

  public static serialize(params: TeamTemplateFilterParametersParams): TeamTemplateFilterParametersJSON {
    const parsed = TeamTemplateFilterParametersSchema.validate(params);
    return this.codec.encode(parsed);
  }
}

export class TeamTemplateOrderParametersSchema {
  public static readonly type = z.custom<TeamTemplateOrderParameters>(
    (data) => data instanceof TeamTemplateOrderParameters,
    "Not a valid TeamTemplateOrderParameters instance"
  );
  public static readonly TeamTemplateOrderParametersJSONZ = z.object({
    orderBy: z.enum(TeamTemplateOrderField).nullish(),
    orderDir: z.enum(OrderDirection).nullish(),
  });
  public static readonly TeamTemplateOrderParametersZ = z.object({
    orderBy: z.enum(TeamTemplateOrderField).nullish(),
    orderDir: z.enum(OrderDirection).nullish(),
  });

  public static readonly codec = z.codec(
    TeamTemplateOrderParametersSchema.TeamTemplateOrderParametersJSONZ,
    TeamTemplateOrderParametersSchema.TeamTemplateOrderParametersZ,
    {
      decode: (data) => ({
        orderBy: data.orderBy,
        orderDir: data.orderDir,
      }),
      encode: (params) => ({
        orderBy: params.orderBy,
        orderDir: params.orderDir,
      }),
    }
  );

  public static validate(values: unknown): TeamTemplateOrderParametersParams {
    return parseOrThrow(TeamTemplateOrderParametersSchema.TeamTemplateOrderParametersJSONZ, values, {
      logError: true,
      schemaName: "TeamTemplateOrderParametersSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static deserialize(json: unknown): TeamTemplateOrderParametersParams | null | undefined {
    const parsed = parseOrThrow(TeamTemplateOrderParametersSchema.TeamTemplateOrderParametersJSONZ, json, {
      logError: true,
      schemaName: "TeamTemplateOrderParametersSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return TeamTemplateOrderParametersSchema.codec.decode(parsed);
  }

  public static serialize(params: TeamTemplateOrderParametersParams): TeamTemplateOrderParametersJSON {
    const parsed = TeamTemplateOrderParametersSchema.validate(params);
    return parsed;
  }
}

export default class TeamTemplateQuerySchema {
  public static readonly TeamTemplateQueryJSONZ = z.object({
    filterParameters: TeamTemplateFilterParametersSchema.TeamTemplateFilterParametersJSONZ.optional(),
    orderParameters: TeamTemplateOrderParametersSchema.TeamTemplateOrderParametersJSONZ.optional(),
    pagination: PaginationSchema.PaginationJSONZ.optional(),
  });

  public static readonly TeamTemplateQueryZ = z.object({
    get filterParameters() {
      return z.instanceof(TeamTemplateFilterParameters).optional();
    },
    get orderParameters() {
      return z.instanceof(TeamTemplateOrderParameters).optional();
    },
    get paginationParameters() {
      return z.instanceof(PaginationParameters).optional();
    },
  });

  public static readonly codec = z.codec(
    TeamTemplateQuerySchema.TeamTemplateQueryJSONZ,
    TeamTemplateQuerySchema.TeamTemplateQueryZ,
    {
      decode: (data) => ({
        filterParameters: TeamTemplateFilterParameters.fromJSON(data.filterParameters) ?? undefined,
        orderParameters: TeamTemplateOrderParameters.fromJSON(data.orderParameters) ?? undefined,
        paginationParameters: PaginationParameters.fromJSON(data.pagination),
      }),
      encode: (params) => ({
        filterParameters: params.filterParameters ? params.filterParameters.toJSON() : undefined,
        orderParameters: params.orderParameters ? params.orderParameters.toJSON() : undefined,
        pagination: params.paginationParameters ? params.paginationParameters.toJSON() : undefined,
      }),
    }
  );

  public static validate(values: unknown): TeamTemplateQueryParams {
    return parseOrThrow(TeamTemplateQuerySchema.TeamTemplateQueryZ, values, {
      logError: true,
      schemaName: "TeamTemplateQuerySchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): TeamTemplateQueryJSON {
    return parseOrThrow(TeamTemplateQuerySchema.TeamTemplateQueryJSONZ, json, {
      logError: true,
      schemaName: "TeamTemplateQuerySchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is TeamTemplateQueryParams {
    try {
      TeamTemplateQuerySchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is TeamTemplateQueryJSON {
    try {
      TeamTemplateQuerySchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): TeamTemplateQueryParams | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(TeamTemplateQuerySchema.TeamTemplateQueryJSONZ, json, {
      logError: true,
      schemaName: "TeamTemplateQuerySchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return TeamTemplateQuerySchema.codec.decode(parsed);
  }

  public static serialize(params: TeamTemplateQueryParams): TeamTemplateQueryJSON {
    if (params === null || params === undefined) return params as TeamTemplateQueryJSON;
    const parsed = TeamTemplateQuerySchema.validate(params);
    if (parsed === null || parsed === undefined) return parsed as TeamTemplateQueryJSON;
    return TeamTemplateQuerySchema.codec.encode(parsed);
  }
}